import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeftIcon, BookOpenIcon, LogOutIcon, MailIcon, MessageCircleIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import logoTozenAI from "@/assets/logos/logo_tozenai.png";
import Button from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import ContactModal from "@/components/ui/ContactModal";
import Modal from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";
import { useLicense } from "@/context/LicenseContext";
import { LICENSING_CONTACT_EMAIL } from "@/lib/constants";

export default function NoLicensePage() {
  const { user, logout } = useAuth();
  const { license, isActive } = useLicense();
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);
  const prefersReduced = useReducedMotion();

  const isExpired = license?.reason === "expired";
  const orgName = license?.organization_name;

  const headline = isActive
    ? "Your license is active"
    : isExpired
      ? "Your license has expired"
      : "No active license for this account";

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg-app px-4 py-10">
      <motion.div
        initial={prefersReduced ? undefined : { opacity: 0, y: 8 }}
        animate={prefersReduced ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        <Card className="flex flex-col items-center gap-6 text-center">
          <img src={logoTozenAI} alt="tozenAI" className="h-8 w-auto" />

          <div className="flex flex-col gap-2">
            <h1 className="font-body text-xl font-normal text-text-primary">{headline}</h1>
            <p className="font-body text-sm text-text-secondary">
              {isActive ? (
                // Landed here spuriously (e.g. a stray 402/403 during a
                // backend deploy window) while the license check passes —
                // don't show lockout copy, offer the way back in.
                "Your license is active — you may have hit a temporary error. Head back into the app and try again."
              ) : (
                <>
                  {orgName ? (
                    <>
                      <span className="font-medium text-text-primary">{orgName}</span> does not
                      currently have an active tozenAI license.
                    </>
                  ) : (
                    "Your account is not covered by an active tozenAI license."
                  )}{" "}
                  Licensing is handled per customer — reach out and we&rsquo;ll get you set up.
                </>
              )}
            </p>
          </div>

          {isActive ? (
            <div className="flex w-full flex-col gap-3">
              <Button variant="primary" className="w-full" onClick={() => navigate("/home")}>
                <ArrowLeftIcon size={16} />
                Back to app
              </Button>
            </div>
          ) : (
            <div className="flex w-full flex-col gap-3">
              <Button
                variant="primary"
                className="w-full"
                onClick={() => {
                  window.location.href = `mailto:${LICENSING_CONTACT_EMAIL}`;
                }}
              >
                <MailIcon size={16} />
                Email {LICENSING_CONTACT_EMAIL}
              </Button>
              <Button variant="secondary" className="w-full" onClick={() => setContactOpen(true)}>
                <MessageCircleIcon size={16} />
                Use the contact form
              </Button>
              {/* Documentation is behind ProtectedRoute, not LicensedRoute, so it
                  stays readable while the licence is being sorted out. */}
              <Button variant="tertiary" className="w-full" onClick={() => navigate("/product-documentation")}>
                <BookOpenIcon size={16} />
                Read the documentation
              </Button>
            </div>
          )}

          <button
            type="button"
            onClick={() => logout()}
            className="inline-flex items-center gap-2 font-body text-sm text-text-muted transition-colors duration-150 hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <LogOutIcon size={14} />
            {user?.email ? `Log out (${user.email})` : "Log out"}
          </button>
        </Card>
      </motion.div>

      <Modal isOpen={contactOpen} onClose={() => setContactOpen(false)} title={null} wide>
        <ContactModal onClose={() => setContactOpen(false)} />
      </Modal>
    </div>
  );
}
